import * as React from "react";
import { Slot } from "@radix-ui/react-slot";
import { cva, type VariantProps } from "class-variance-authority";

import { cn } from "@/lib/utils";

const buttonVariants = cva(
  // Every button answers the pointer (UX-pass rule): hover lifts 1px, press
  // scales down. The cyan the hover adds is a *hairline* — a 1.5px ring on a
  // filled button, the border itself on an outline one — never the blurred
  // 2.5px halo this used to paint, which read as a dated glow beside the
  // crisp outline hover. `link` opts out of the lift entirely.
  "inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md text-sm font-medium transition-all duration-150 focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring focus-visible:ring-offset-0 disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none [&_svg]:size-4 [&_svg]:shrink-0 hover:-translate-y-px active:translate-y-0 active:scale-[0.96] active:shadow-none",
  {
    variants: {
      variant: {
        default: "bg-primary text-primary-foreground hover:bg-primary/90 hover:shadow-[0_0_0_1.5px_hsl(var(--ring))]",
        // OUTLINED, not filled — the bulk bar's delete set this precedent and
        // it is now the whole app's. A solid red button is the loudest object
        // on any surface it lands on, and it lands on confirmation dialogs,
        // where it is sitting beside a Cancel the reader is *more* likely to
        // want: the shouting was pointed at the wrong choice. Outlined, it
        // still reads as destructive (red is the only red on the dialog) and
        // it leaves somewhere to GO — the hold-to-confirm fill sweeps into it,
        // which a button already filled with that same red could not show.
        destructive:
          "border border-destructive/60 bg-transparent text-destructive hover:border-destructive hover:bg-destructive/5",
        // The clean outline hover (matches the backlog's "Group:" pill): the
        // border itself goes brand-cyan, and nothing sits behind it.
        outline:
          "border border-input bg-background text-foreground hover:bg-secondary hover:border-[hsl(var(--ring))]",
        secondary: "bg-secondary text-secondary-foreground hover:bg-secondary/80",
        ghost: "hover:bg-accent hover:text-accent-foreground",
        link: "text-brand underline-offset-4 hover:underline hover:translate-y-0 hover:shadow-none active:scale-100",
      },
      size: {
        default: "h-10 px-4 py-2",
        sm: "h-9 rounded-md px-3",
        lg: "h-11 rounded-md px-8",
        icon: "h-10 w-10",
      },
    },
    defaultVariants: {
      variant: "default",
      size: "default",
    },
  },
);

export interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {
  asChild?: boolean;
}

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  ({ className, variant, size, asChild = false, ...props }, ref) => {
    const Comp = asChild ? Slot : "button";
    return <Comp className={cn(buttonVariants({ variant, size, className }))} ref={ref} {...props} />;
  },
);
Button.displayName = "Button";

export { Button, buttonVariants };
